import { Translations } from '@domain/admission';
import { TFunction } from 'i18next';

export interface Channel {
  id: string;
  maxQuantity: string | number | null;
}

export interface OfferTeam {
  offerId: string;
  teamId: string;
  teamName: string;
  quantity: number | null;
}

export interface SaveOfferTeamMaxQuantityRequest {
  offerId: string;
  teamId: string;
  maxQuantity: number | null;
}

/** Offer-service form-field write contract — shared by admissions, add-ons, passes and invitations. */
export interface FormField {
  id?: string;
  name: string;
  slug?: string | null;
  dataType: string;
  isRequired: boolean;
  isActive?: boolean;
  deleted?: boolean;
  max?: number;
  min?: number;
}

export interface CreateAdmissionRequest {
  eventId?: string;
  price?: {
    amount?: number;
    currency: string;
    onlinePrice?: number;
    offlinePrice?: number;
  };
  isDraft?: boolean;
  quantity?: number | null;
  salesTime?: {
    openingTime?: number | null;
    closingTime?: number;
  };
  offerReleasedBefore?: string | null;
  name?: Translations;
  description?: Translations;
  addOns?: string[];
  salesStatus?: {
    isSoldOut: boolean;
    isSalesClosed: boolean;
  };
  itemsPerTransaction?: {
    min: number;
    max: number;
  };
  onlinePaymentPercentage?: number;
  guestCapacity?: number;
  extraGuestMaxQuantity?: number | null;
  pricePerExtraGuest?: number | null;
  settings?: {
    isHidden?: boolean;
    isPrivate?: boolean;
    distribution?: 'online' | 'offline' | 'online_and_offline';
    isForAllChannels?: boolean;
    isNominative?: boolean;
    italyRegulation?: {
      dirittoPrevendita: number | null;
      isSiaeExcluded: boolean;
      isSiaeApproved?: boolean;
      allows18App?: boolean;
      needsUserVerified?: boolean;
      abbonamentoId?: string | null;
    } | null;
    channelDefaultMaxQuantity?: number | null;
  };
  channels?: Channel[];
  bookingFormFields?: FormField[];
  ticketHolderFormFields?: FormField[];
  accessCode?: string | null;
  floorplanId?: string;
  drinksIncluded?: number;
  checkInTime?: {
    validFrom?: number | null;
    validUntil?: number | null;
  };
  color?: string | null;
  hasMandatoryAddOns?: boolean;
  addOnChoiceMode?: string;
  vatRate?: number | null;
  ticketsLeftThreshold?: number | null;
  offerPresetId?: string;
}

export interface Action {
  name: string;
  action: () => void;
}

export const ADMISSION_CARD_TYPE = {
  pass: 'pass',
  offer: 'offer',
} as const;

export interface AdmissionCardActionsConfig {
  type: (typeof ADMISSION_CARD_TYPE)[keyof typeof ADMISSION_CARD_TYPE];
  isPreset: boolean;
  isPastEvent?: boolean;
  isSharePaymentLinkActive: boolean;
  isOpenForAll?: boolean;
  deleteIsUnassign?: boolean;
  isGroupCard?: boolean;
  isEnabledSection?: boolean;
  isToggleActive?: boolean;
  isExportQrCodesActive?: boolean;

  // Available callbacks
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeletePass?: (id: string) => void;
  onDuplicate?: () => void;
  onUnassign?: (id: string) => void;
  onClickViewHolders?: () => void;
  onClickAddHolder?: () => void;
  onClickViewEvents?: () => void;
  onClickExportQrCodes?: () => void;

  // Action handlers (already wrapped in useCallback from parent)
  handleEdit: () => void;
  handleDelete: () => void;
  handleDeletePass: () => void;
  handleDuplicate: () => void;
  handleUnassign: () => void;
  handleViewHolders: () => void;
  handleAddHolder: () => void;
  handleViewEvents: () => void;
  handleExportQrCodes: () => void;
  handleShareLink: () => void;
  handleAddGroup: () => void;
  handleRemoveFromGroup: () => void;
  handleMoveToAnotherGroup: () => void;
  // i18n
  t: TFunction;
}

export interface ActionBuilderContext {
  actions: Action[];
  config: AdmissionCardActionsConfig;
  t: TFunction;
}
